import React from 'react';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <span className="text-3xl">🐾</span>
              <img 
                src="https://pub-b9622d81b0bd442fb020f880d8cec37d.r2.dev/petrecord-logo.png" 
                alt="PetRecord.ai" 
                className="h-8"
              />
            </a>
            <a href="/" className="text-blue-600 hover:text-blue-800 font-medium">
              ← Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Terms of Service Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Terms of Use</h1>
          
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> October 30, 2025<br />
            <strong>Last Updated:</strong> October 30, 2025<br />
            <strong>Entity:</strong> M1 Ventures dba PetRecord.ai ("PetRecord.ai", "we", "us", "our")<br />
            <strong>Website:</strong> https://petrecord.ai
          </p>

          <p className="mb-6">
            Please read these Terms of Use ("Terms") carefully. By accessing or using the Site and Services, you agree to be bound by these Terms. If you do not agree, you may not use the Site or Services.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Service Description & Scope</h2>
          <p className="mb-6">
            PetRecord.ai provides a subscription-based service via our website and connected application (my.petrecord.ai) that allows pet owners to upload and store veterinary records and receive AI-generated summaries based on those records. The Site described herein is the marketing website where subscriptions are purchased; the connected Service is accessed through the app.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Eligibility</h2>
          <p className="mb-6">
            You represent and warrant that you are at least 18 years old and have full power and authority to enter these Terms. If you are registering on behalf of an entity, you represent you have authority to bind that entity.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Accounts, Registration & Subscription</h2>
          <p className="mb-4">
            To access paid Services, you must register an account and provide accurate, complete and up-to-date information. You are solely responsible for your account password and all activity that occurs under your account.
          </p>
          <p className="mb-4">
            You agree to pay all subscription fees for the plan you select. Payment is processed through Stripe and payment terms (renewals, cancellations) are governed by our billing policy as displayed when you subscribe.
          </p>
          <p className="mb-4">
            We may change prices, plans, or features at our discretion; we will notify you in accordance with our billing policy.
          </p>
          <p className="mb-6">
            We reserve the right to suspend or terminate your account if you fail to comply with these Terms or if payments are delinquent.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. User Content & Uploads</h2>
          <p className="mb-4">
            "User Content" refers to the veterinary records and any other information, files or materials that you upload through the Service. You retain all ownership rights in your User Content.
          </p>
          <p className="mb-4">
            By uploading User Content you grant PetRecord.ai a worldwide, non-exclusive, royalty-free, sublicensable, revocable license to host, store, process (including AI summarisation), display and transmit your User Content solely for the purpose of providing the Service.
          </p>
          <p className="mb-4">
            You represent and warrant that you have all necessary rights and permissions to upload the records, and your User Content does not infringe any third-party rights or applicable laws.
          </p>
          <p className="mb-6">
            You are responsible for maintaining backup copies of your records; you acknowledge we are not responsible for loss of your data.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. No Veterinary Advice or Emergency Use</h2>
          <p className="mb-4">
            The Service is for informational and organisational purposes only. It is not veterinary advice, diagnosis, or treatment. The AI-generated summaries are derived from your records but may contain errors or omissions and should not be relied upon for care decisions. You remain solely responsible for reviewing original records and consulting a licensed veterinarian for any care decisions.
          </p>
          <p className="mb-6">
            In a medical emergency (for example: difficulty breathing, severe bleeding, suspected toxin ingestion, bloat in dogs, seizures), you must contact a licensed emergency veterinarian immediately. Do not use the Service in emergencies.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Acceptable Use</h2>
          <p className="mb-4">You agree not to use the Service for any unlawful purpose, or in any way that violates these Terms or applicable law. Prohibited uses include, but are not limited to:</p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Uploading records you are not authorised to share.</li>
            <li>Uploading malicious, infringing, defamatory, or unlawful content.</li>
            <li>Interfering with the Service or attempting to reverse engineer it.</li>
            <li>Using the Service to provide veterinary care, diagnosis or treatment.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Intellectual Property</h2>
          <p className="mb-6">
            All rights, title and interest in and to the Site, the Service, software, design, graphics, trademarks and content ("Materials") are owned by PetRecord.ai or its licensors. You may not copy, reproduce, modify or distribute the Materials except as permitted under these Terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Third-Party Services</h2>
          <p className="mb-6">
            We integrate with third-party services such as Stripe (payments), Supabase (data storage), analytics providers and others. Your use of such third-party services may be subject to their terms and policies. We are not responsible for the conduct or policies of third parties.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Disclaimers and Release</h2>
          <p className="mb-6">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Limitation of Liability</h2>
          <p className="mb-6">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, PetRecord.ai, its officers, directors, employees and agents will not be liable for any indirect, incidental, special, punitive or consequential damages arising from your use or inability to use the Service, whether or not we knew such damages might occur. OUR AGGREGATE LIABILITY FOR ANY CLAIM ARISING FROM OR RELATING TO THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 12 MONTHS PRIOR TO THE EVENT GIVING RISE TO LIABILITY.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Indemnification</h2>
          <p className="mb-6">
            You will defend, indemnify and hold harmless PetRecord.ai, its affiliates, officers, directors, employees and agents from any claims, liabilities, losses, costs or expenses (including attorneys' fees) arising out of your violation of these Terms or your use of the Service.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Termination</h2>
          <p className="mb-6">
            We may suspend or terminate your account, prohibit access to the Service or remove content at our discretion, with or without notice, for any reason, including breach of these Terms. Sections on disclaimers, limitations, intellectual property and indemnification will survive termination.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Governing Law & Dispute Resolution</h2>
          <p className="mb-6">
            These Terms are governed by the laws of the State of Utah, without regard to conflict-of-law principles. You and we agree that the state and federal courts located in Utah County, Utah shall have exclusive jurisdiction over any dispute arising under or related to these Terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Changes to Terms</h2>
          <p className="mb-6">
            We may modify these Terms at any time. We will update the "Last Updated" date above and notify you of material changes by posting on the Site or emailing you. Your continued use of the Site after such changes constitutes acceptance of the updated Terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. Contact Information</h2>
          <p className="mb-4">If you have questions or concerns about these Terms, please contact us at:</p>
          <p className="mb-6">
            M1 Ventures dba PetRecord.ai<br />
            help@petrecord.ai<br />
            <br />
            Address:<br />
            11757 W Ken Caryl Ave, #272, Littleton Co 80127
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;